import React, { useEffect, useMemo } from "react";

import { css } from "@emotion/react";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { ContentfulComponentCMSHook } from "@swo/shared";
import { CSS_MQ } from "../../styles/breakpoints";

import { PageHeadlineMedium } from "../_shared-components/ui-elements/PageHeadline";
import { ContentContainer } from "../_shared-components/arrangement/Container";
import { SwarovskiButton } from "../_shared-components/ui-elements/SwarovskiButton";
import { ProductTeaserElementContents } from "../../layouts/product-comparison-page-layout/elements/PCPProductTeaser";
import { usePageContext } from "../../global/page-context";
import { Attribute } from "../../layouts/product-technical-specification-page-layout/sections/list-section";
import { SwarovskiGenericCarouselComponent } from "../_shared-components/ui-elements/SwarovskiGenericCarouselComponent/SwarovskiGenericCarouselComponent";
import { CSS_COLORS, CSS_SPACER_SIZES } from "../../styles/styles-css-variables";
import { Spacer } from "../_shared-components/arrangement/Space";
import { getCleanProductComparisonPageLink } from "../../layouts/product-comparison-page-layout/elements/state";
import { pipe } from "fp-ts/lib/function";
import { nonEmptyArray } from "fp-ts";
import { fontColor, FONTS } from "../../styles/fonts";
import { getComponentCMSClassname } from "../cms/edit-overlay/component-hook-class";
import { ComponentTechSpecTeaserType, SwarovskiOptikLocaleFrontend } from "@swo/storefront";
import { useCurrentWidth } from "../../hooks/use-current-width";
import { useEqualState } from "../../hooks";
import { useDeviceScreenWidth, useWindowRelatedInfo } from "../../hooks/use-device-screen-height";
import { productNameSort } from "@swo/shared";
import { SwarovskiLink } from "../_shared-components/ui-elements/SwarovskiLink";
import { formatOptionalStartingPrice } from "../../domain/swarovski-optik/products/price/format-price";
import { NewLabel2 } from "../../layouts/product-detail-page-layout/NewLabel2";
import { T } from "../T";

export const TechSpecTeaserComponent: ReactTFC<
  ComponentTechSpecTeaserType & { localeForNumberFormat: SwarovskiOptikLocaleFrontend } & ContentfulComponentCMSHook
> = props => {
  const ctx = usePageContext();
  const fromPriceLabel = ctx.generalTranslations("fromPriceLabel");
  const lowestPriceLast30DaysTranslation = ctx.generalTranslations("lowestPriceLast30Days");

  const [imageWidth, setImageWidth] = useEqualState<number>(1400 / 4);
  const currentWidth = useCurrentWidth();
  const screenWidth = useDeviceScreenWidth();

  useEffect(() => {
    if (currentWidth && currentWidth === "xs" && screenWidth) {
      setImageWidth(screenWidth - 40 - 30);
    }
  }, [screenWidth]);

  const carouselElements = useMemo(() => {
    return [...props.products]
      .sort((a, b) => productNameSort(a.name, b.name))
      .map(product => {
        const priceLabel = formatOptionalStartingPrice(
          product.price
            ? {
                startingPrice: product.price,
                discountedStartingPrice: product.discountedStartingPrice,
                lowestPrice30Days: product.lowestPrice30Days,
                discountPercentage: product.discountPercentage,
              }
            : undefined,
          fromPriceLabel,
          lowestPriceLast30DaysTranslation,
          props.localeForNumberFormat,
        );

        return (
          <div
            key={product.key}
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;

              padding-left: 15px;
              padding-right: 15px;
            `}
          >
            <ProductTeaserElementContents
              image={
                product.image
                  ? {
                      url: product.image.url,
                      alt: product.image.description || product.image.title,
                      sys: { id: product.image.sys.id },
                    }
                  : undefined
              }
              startingPriceLabel={undefined}
              ctaButton={{ link: product.link, label: props.buyLabel }}
            >
              <SwarovskiLink
                isInternal={true}
                link={product.link}
                scrollToTop={true}
                styled={"no-decoration"}
                attachStyles={css`
                  ${FONTS.SwarovskiFutura500};

                  font-size: 24px;
                  color: ${CSS_COLORS.Green};
                  letter-spacing: 0.8px;
                  text-align: center;

                  margin-bottom: 10px;

                  min-height: 2em;
                  ${CSS_MQ.atLeastMd} {
                    min-height: 3em;
                  }
                `}
              >
                <NewLabel2 name={product.name} isNew={true} />
              </SwarovskiLink>

              <div
                css={css`
                  width: 100%;

                  border-top: 1px solid black;
                  padding-top: 20px;

                  padding-bottom: 20px;
                  border-bottom: 1px solid black;
                `}
              >
                {product.attributes.map(({ label, values, __key }) => {
                  return <Attribute key={__key} label={label} values={values} highlighted={false} />;
                })}
              </div>

              <span
                css={[
                  FONTS.SwarovskiFutura500,
                  fontColor.black,
                  css`
                    margin-top: 25px;
                    margin-bottom: 20px;

                    font-size: 24px;
                    letter-spacing: 0.8px;
                    text-align: center;
                  `,
                ]}
              >
                <T forcedValue={fromPriceLabel}>{priceLabel}</T>
              </span>
            </ProductTeaserElementContents>
          </div>
        );
      });
  }, [props.products, props.localeForNumberFormat, fromPriceLabel]);

  const compareProductLink = getCleanProductComparisonPageLink(
    props.compareProduct.link,
    pipe(props.products, nonEmptyArray.head).key,
  );

  return (
    <ContentContainer className={getComponentCMSClassname(props?.sys?.id)}>
      <PageHeadlineMedium>
        <T>{props.headline}</T>
      </PageHeadlineMedium>
      <Spacer size={"md"} />

      <SwarovskiGenericCarouselComponent
        key={imageWidth}
        width={"content"}
        headline={null}
        imageWidth={imageWidth}
        scrollSnap={true}
        bottomText={undefined}
        carouselElements={carouselElements}
      />

      <div
        css={css`
          margin-top: ${CSS_SPACER_SIZES.smd};

          display: flex;

          ${CSS_MQ.atMostSm} {
            flex-direction: column;
            align-items: center;

            > * + * {
              margin-top: 30px;
            }
          }

          ${CSS_MQ.atLeastMd} {
            justify-content: center;

            > * + * {
              margin-left: 50px;
            }
          }
        `}
      >
        <SwarovskiButton
          variant={"plain-underline"}
          scrollToTop={true}
          as={"a"}
          isInternal={true}
          link={compareProductLink}
        >
          <T>{props.compareProduct.label}</T>
        </SwarovskiButton>
      </div>
    </ContentContainer>
  );
};
